// R1c.3a-v3 — Bid Proposals list page. Owner-driven upload flow.
// Distinct from /crm/proposals (Phase 2 auto-generated proposal-to-client).
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import { Upload, FileText, Plus } from "lucide-react";

const STATUS_LABELS = {
  draft: "Draft", submitted: "Submitted", pending_approval: "Pending Approval",
  awarded: "Awarded", rejected: "Rejected", withdrawn: "Withdrawn", expired: "Expired",
};

const STATUS_TO_CHIP = {
  draft: "draft", submitted: "connecting", pending_approval: "connecting",
  awarded: "connected", rejected: "error", withdrawn: "not_configured", expired: "degraded",
};

const money = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function BidProposals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/bid-proposals")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => statusFilter === "all"
    ? items
    : items.filter(i => i.status === statusFilter), [items, statusFilter]);

  return (
    <AdminAppShell
      activeNav="crm"
      title="CRM · Bid Proposals"
      subtitle={`${items.length} bid proposal(s) · Upload → Submit → Awarded → Kickoff project`}
      breadcrumbs={[{ label: "CRM", to: "/crm/leads" }, { label: "Bid Proposals", to: null }]}
      user={user}
      actions={
        <Link to="/crm/bid-proposals/new" data-testid="bid-proposals-upload-link">
          <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="bid-proposals-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> Upload proposal
          </Button>
        </Link>
      }
    >
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="bid-proposals-filters">
        {["all", ...Object.keys(STATUS_LABELS)].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded border ${
              statusFilter === s
                ? "bg-erf-dark text-erf-cream border-erf-dark"
                : "bg-white text-erf-dark border-erf-borderCream hover:border-erf-gold"
            }`}
            data-testid={`bid-proposals-filter-${s}`}
          >
            {s === "all" ? "All" : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-erf-dark/60 font-nav" data-testid="bid-proposals-loading">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="border border-erf-borderCream bg-white rounded-md p-10 text-center" data-testid="bid-proposals-empty">
          <FileText className="w-10 h-10 mx-auto text-erf-gold mb-3" aria-hidden="true" />
          <div className="font-display text-lg font-semibold text-erf-dark">
            {statusFilter === "all" ? "No proposals yet" : `No ${STATUS_LABELS[statusFilter]} proposals`}
          </div>
          <div className="text-sm font-nav text-erf-dark/70 mt-1 mb-4">
            {statusFilter === "all"
              ? "Upload your first proposal — PDF or DOCX, up to 25 MB."
              : "Try a different filter, or upload a new proposal."}
          </div>
          <Link to="/crm/bid-proposals/new">
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="bid-proposals-empty-cta">
              <Plus className="w-4 h-4 mr-1" /> Upload proposal
            </Button>
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table" data-testid="bid-proposals-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-left px-3 py-2">Submitted To</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(p => (
                <tr key={p.id} className="border-t border-erf-borderCream/60" data-testid={`bid-proposal-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/crm/bid-proposals/${p.id}`} className="font-nav font-semibold hover:text-erf-goldHover" data-testid={`bid-proposal-link-${p.id}`}>
                      {p.project_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px]">{money(p.estimated_value)}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-erf-dark/70">
                    {(p.submitted_to || []).slice(0, 2).join(", ") || "—"}
                    {(p.submitted_to || []).length > 2 && ` +${p.submitted_to.length - 2}`}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-erf-dark/60">
                    {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip status={STATUS_TO_CHIP[p.status] || "not_configured"} testId={`bid-proposal-status-${p.id}`} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/crm/bid-proposals/${p.id}`} data-testid={`bid-proposal-detail-${p.id}`}>
                      <Button size="sm" variant="ghost" className="h-8">Open</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminAppShell>
  );
}
